/**
 * LiveStats — Two clickable stat cards (Discord + GTA5-Mods) shown in the
 * hero when admin has enabled live stats. Numbers refresh every 5 min so
 * a long-open tab stays current.
 *
 * Each card links out to the underlying profile/server when clicked.
 */
import { useEffect, useState } from "react";
import { fetchLiveStats } from "@/api";
import { ExternalLink } from "lucide-react";

const fmtNum = (n) => {
  if (!Number.isFinite(n)) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
};

export default function LiveStats() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const d = await fetchLiveStats();
        if (!cancelled) setData(d);
      } catch { /* silent — fall back to nothing */ }
    };
    load();
    const id = setInterval(load, 5 * 60 * 1000); // refresh client-side every 5 min
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!data || !data.enabled) return null;
  const d = data.discord || {};
  const g = data.gta5mods || {};
  const showDiscord = !!d.invite_url && (d.members > 0 || d.online > 0);
  const showGta = !!g.profile_url && (g.files > 0 || g.downloads > 0);
  if (!showDiscord && !showGta) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="hero-live-stats">
      {showDiscord && (
        <StatCard
          href={d.invite_url}
          testid="live-stat-discord"
          accent="indigo"
          kicker="DISCORD"
          big={fmtNum(d.members)}
          bigLabel="MEMBERS"
          metaTop={`${fmtNum(d.online)} ONLINE NOW`}
          metaBottom={d.guild_name || "JOIN THE SERVER"}
        />
      )}
      {showGta && (
        <StatCard
          href={g.profile_url}
          testid="live-stat-gta"
          accent="amber"
          kicker="GTA5-MODS"
          big={fmtNum(g.files)}
          bigLabel={g.files === 1 ? "RELEASE" : "RELEASES"}
          metaTop={`${fmtNum(g.downloads)} DOWNLOADS`}
          metaBottom="OPEN PROFILE"
        />
      )}
    </div>
  );
}

function StatCard({ href, testid, kicker, big, bigLabel, metaTop, metaBottom, accent }) {
  // accent dot color (semantic, not theme-driven): indigo for Discord, amber for GTA
  const dotColor = accent === "indigo" ? "#5865F2" : accent === "amber" ? "#f59e0b" : "var(--theme-accent-primary)";
  return (
    <a
      data-testid={testid}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative block bg-[var(--theme-surface)]/80 backdrop-blur-sm border border-[var(--theme-border)] hover:border-[var(--theme-accent-primary)] rounded-2xl transition p-5 overflow-hidden hover:shadow-[0_0_30px_-8px_rgba(var(--theme-accent-primary-rgb),0.25)]"
    >
      {/* Subtle accent halo on hover */}
      <div
        className="absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-0 group-hover:opacity-30 transition-opacity duration-500 blur-2xl pointer-events-none"
        style={{ background: dotColor }}
      />

      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--theme-text-muted)]">
          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: dotColor }} />
          {kicker}
        </div>
        <ExternalLink
          size={12}
          className="text-[var(--theme-text-muted)] group-hover:text-[var(--theme-accent-primary)] transition"
        />
      </div>

      <div className="flex items-baseline gap-2 mb-3">
        <span className="font-display text-4xl md:text-5xl font-extrabold tabular-nums tracking-tight leading-none">
          {big}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--theme-text-muted)]">
          {bigLabel}
        </span>
      </div>

      <div className="mt-3 pt-3 border-t border-[var(--theme-border)] space-y-1">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--theme-text)] whitespace-nowrap">
          {metaTop}
        </div>
        <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--theme-text-muted)] group-hover:text-[var(--theme-accent-primary)] transition whitespace-nowrap overflow-hidden text-ellipsis">
          {metaBottom} →
        </div>
      </div>
    </a>
  );
}
